import React from "react";
import manageStorageProfileStyles from "../manage-storage-profile.module.scss";
import TooltipQuestion from "../../../shared-ui/tooltip-question";
import { FormField } from "../../../shared-ui/form/form-field/form-field";
import { FormFieldLabel } from "../../../shared-ui/form/form-field-label/form-field-label";
import { FormFieldValue } from "../../../shared-ui/form/form-field-value/form-field-value";
import { Select } from "../../../shared-ui/select";
import IOption from "../../../shared-ui/common/option";
import Checkbox from "../../../shared-ui/input/checkbox";
import { IPolicyRetention } from "../../../../api/entities/policy";
import SpaceSelector from "../space-selector";
import TimeIntervalSelector from "../time-interval-selector/time-interval-selector";
import styles from "./retention-section.module.scss";
import clsx from "clsx";
import inputStyles from "../../../shared-ui/styles/input.module.scss";
import SidePanelSection from "../../../shared-ui/side-panel-section/side-panel-section";
import { enumUtils } from "../../../../utils/enum-utils";

export enum RetentionPolicy {
  DeleteOnly = 1,
  RetainAndThenDelete = 2,
  RetainOnly = 3,
}

export const retentionPolicyToTextMap = {
  [RetentionPolicy.DeleteOnly]: "Delete-only",
  [RetentionPolicy.RetainAndThenDelete]: "Retain and then Delete",
  [RetentionPolicy.RetainOnly]: "Retain-only"
};

export interface IRetentionSettingsErrors {
  deletedFilesMb?: string;
  revisionsMb?: string;
  deletedFilesDays?: string;
  revisionsDays?: string;
  retention?: string;
}
export interface IRetentionSettings {
  deletedFilesMode: RetentionPolicy;
  isDeletedFilesMbEnabled: boolean;
  isDeletedFilesDaysEnabled: boolean;
  deletedFilesDays: number;
  deletedFilesMb: number;

  revisionsMode: RetentionPolicy;
  isRevisionsMbEnabled: boolean;
  isRevisionsDaysEnabled: boolean;
  revisionsDays: number;
  revisionsMb: number;
}

function getRetentionMode(
  isRetentionEnabled: boolean,
  retentionMegabytes: number,
  retentionDays: number
) {
  if (isRetentionEnabled) {
    if (retentionMegabytes === 0 && retentionDays === 0) {
      return RetentionPolicy.DeleteOnly;
    }
    if (retentionMegabytes || retentionDays) {
      return RetentionPolicy.RetainAndThenDelete;
    }
  }
  return RetentionPolicy.RetainOnly;
}

export function isRetentionSettingsValid(
  settings: IRetentionSettings
): boolean {
  if (settings.deletedFilesMode === null || settings.revisionsMode === null) {
    return false;
  }

  if (
    settings.deletedFilesMode === RetentionPolicy.RetainAndThenDelete &&
    ((settings.isDeletedFilesDaysEnabled && settings.deletedFilesDays <= 0) ||
      (settings.isDeletedFilesMbEnabled && settings.deletedFilesMb <= 0))
  ) {
    return false;
  }

  if (
    settings.revisionsMode === RetentionPolicy.RetainAndThenDelete &&
    ((settings.isRevisionsDaysEnabled && settings.revisionsDays <= 0) ||
      (settings.isRevisionsMbEnabled && settings.revisionsMb <= 0))
  ) {
    return false;
  }

  return true;
}

export function getRetentionSettingsFromPolicy(
  policy: IPolicyRetention
): IRetentionSettings {
  const result = {
    deletedFilesMode: getRetentionMode(
      policy.retainDeletedFiles,
      policy.deletedFilesRetentionMegabytes,
      policy.deletedFilesRetentionDays
    ),
    revisionsMode: getRetentionMode(
      policy.retainPreviousVersion,
      policy.previousVersionRetentionMegabytes,
      policy.previousVersionRetentionDays
    ),

    isDeletedFilesMbEnabled:
      policy.retainDeletedFiles && policy.deletedFilesRetentionMegabytes > 0,
    isRevisionsMbEnabled:
      policy.retainPreviousVersion &&
      policy.previousVersionRetentionMegabytes > 0,

    isDeletedFilesDaysEnabled:
      policy.retainDeletedFiles && policy.deletedFilesRetentionDays > 0,
    isRevisionsDaysEnabled:
      policy.retainPreviousVersion && policy.previousVersionRetentionDays > 0,

    deletedFilesDays: policy.deletedFilesRetentionDays || null,
    revisionsDays: policy.previousVersionRetentionDays || null,

    deletedFilesMb: policy.deletedFilesRetentionMegabytes || null,
    revisionsMb: policy.previousVersionRetentionMegabytes || null,
  };

  if (!result.isDeletedFilesMbEnabled && !result.isDeletedFilesDaysEnabled) {
    result.isDeletedFilesMbEnabled = true;
    result.isDeletedFilesDaysEnabled = true;
  }

  if (!result.isRevisionsMbEnabled && !result.isRevisionsDaysEnabled) {
    result.isRevisionsMbEnabled = true;
    result.isRevisionsDaysEnabled = true;
  }

  return result;
}

export function getRetentionSettingsForPolicy(
  settings: IRetentionSettings
): IPolicyRetention {
  return {
    retainDeletedFiles:
      settings.deletedFilesMode === RetentionPolicy.RetainAndThenDelete ||
      settings.deletedFilesMode === RetentionPolicy.DeleteOnly,
    retainPreviousVersion:
      settings.revisionsMode === RetentionPolicy.RetainAndThenDelete ||
      settings.revisionsMode === RetentionPolicy.DeleteOnly,

    deletedFilesRetentionMegabytes:
      settings.deletedFilesMode === RetentionPolicy.DeleteOnly
        ? 0
        : settings.isDeletedFilesMbEnabled
        ? settings.deletedFilesMb
        : null,
    previousVersionRetentionMegabytes:
      settings.revisionsMode === RetentionPolicy.DeleteOnly
        ? 0
        : settings.isRevisionsMbEnabled
        ? settings.revisionsMb
        : null,

    deletedFilesRetentionDays:
      settings.deletedFilesMode === RetentionPolicy.DeleteOnly
        ? 0
        : settings.isDeletedFilesDaysEnabled
        ? settings.deletedFilesDays
        : null,
    previousVersionRetentionDays:
      settings.revisionsMode === RetentionPolicy.DeleteOnly
        ? 0
        : settings.isRevisionsDaysEnabled
        ? settings.revisionsDays
        : null,
  };
}

const retentionOptions: IOption[] = enumUtils.values(RetentionPolicy).map((retentionMode) => ({
  value: retentionMode,
  label: retentionPolicyToTextMap[retentionMode]
}));

const REVISIONS_SPACE_TOOLTIP =
    "Myota will permanently delete your file version history once it exceed the defined space.",
  REVISIONS_TIME_TOOLTIP =
    "Myota will permanently delete your file version history once it exceed the allotted timeframe.",
  DELETED_FILES_SPACE_TOOLTIP =
    "Myota will permanently delete recoverable deleted files once they exceed the defined space.",
  DELETED_FILES_TIME_TOOLTIP =
    "Myota will permanently delete recoverable deleted files once they exceed the allotted timeframe.";

interface IRetensionSectionProps extends IRetentionSettings {
  autoFocus?: boolean;
  errors: IRetentionSettingsErrors;
  disabled?: boolean;
  onChange: (value: Partial<IRetentionSettings>) => void;
  onError: (errors: Partial<IRetentionSettingsErrors>) => void;
}

export function RetentionSection(props: IRetensionSectionProps) {
  function onFieldChanged(value: Partial<IRetentionSettings>) {
    props.onChange(value);
    validateField(Object.keys(value)[0] as keyof IRetentionSettings, value);
  }

  function validateField(
    fieldName: keyof IRetentionSettings,
    value: Partial<IRetentionSettings>
  ) {
    switch (fieldName) {
      case "deletedFilesMb":
      case "revisionsMb":
      case "deletedFilesDays":
      case "revisionsDays":
        props.onError({
          [fieldName]:
            value[fieldName] <= 0
              ? "Entered value must be more than '0'"
              : null,
        });
    }
  }

  return (
    <SidePanelSection
      title="Retention Policy"
      testId="storage-retention"
      className={clsx(styles["root"], manageStorageProfileStyles["input--default"])}
    >
      <FormField testId="deletion-retention">
        <div
          data-testid="storage-retention-description"
          className={manageStorageProfileStyles.question}
        >
          How would you like to handle a user’s file deletion?
          <TooltipQuestion>
            When a user deletes files, Myota can allow them to recover these
            files. Parameters for permanent deletion can be set.
          </TooltipQuestion>
        </div>
        <FormFieldValue className={styles["retention-mode"]}>
          <Select
            testId="retention-mode"
            value={props.deletedFilesMode}
            placeholder="Choose one.."
            autoFocus={props.autoFocus}
            disabled={props.disabled}
            options={retentionOptions}
            onChange={deletedFilesMode => onFieldChanged({ deletedFilesMode })}
          />
        </FormFieldValue>
      </FormField>

      {props.deletedFilesMode === RetentionPolicy.RetainAndThenDelete && (
        <>
          <FormField
            className={clsx(
              styles["retention-param"],
              !props.isDeletedFilesMbEnabled &&
                styles["retention-param--disabled"]
            )}
          >
            <FormFieldLabel className={styles["retention-param-label"]}>
              <Checkbox
                testId="retention-size"
                disabled={props.disabled}
                label="Automatically clear deleted files when they exceed:"
                checked={props.isDeletedFilesMbEnabled}
                onChange={value =>
                  onFieldChanged({
                    isDeletedFilesMbEnabled:
                      value || !props.isDeletedFilesDaysEnabled,
                  })
                }
              />
              <TooltipQuestion>{DELETED_FILES_SPACE_TOOLTIP}</TooltipQuestion>
            </FormFieldLabel>
            <FormFieldValue className={styles["retention-param-value"]}>
              <SpaceSelector
                testId="deleted-files-exceed-selector"
                hideMb={true}
                value={props.deletedFilesMb}
                onChange={deletedFilesMb => onFieldChanged({ deletedFilesMb })}
                disabled={props.disabled || !props.isDeletedFilesMbEnabled}
                tooltip={DELETED_FILES_SPACE_TOOLTIP}
                className={clsx(
                  props.errors.deletedFilesMb && inputStyles.error
                )}
              />
              {props.errors.deletedFilesMb && (
                <div className="error-text">{props.errors.deletedFilesMb}</div>
              )}
            </FormFieldValue>
          </FormField>

          <FormField
            className={clsx(
              styles["retention-param"],
              !props.isDeletedFilesDaysEnabled &&
                styles["retention-param--disabled"]
            )}
          >
            <FormFieldLabel className={styles["retention-param-label"]}>
              <Checkbox
                testId="retention-age"
                disabled={props.disabled}
                label="Automatically clear deleted files older than:"
                checked={props.isDeletedFilesDaysEnabled}
                onChange={value =>
                  onFieldChanged({
                    isDeletedFilesDaysEnabled:
                      value || !props.isDeletedFilesMbEnabled,
                  })
                }
              />
              <TooltipQuestion>{DELETED_FILES_TIME_TOOLTIP}</TooltipQuestion>
            </FormFieldLabel>
            <FormFieldValue className={styles["retention-param-value"]}>
              <TimeIntervalSelector
                tooltip={DELETED_FILES_TIME_TOOLTIP}
                value={props.deletedFilesDays}
                disabled={props.disabled || !props.isDeletedFilesDaysEnabled}
                onChange={deletedFilesDays =>
                  onFieldChanged({ deletedFilesDays })
                }
              />
              {props.errors.deletedFilesDays && (
                <div className="error-text">
                  {props.errors.deletedFilesDays}
                </div>
              )}
            </FormFieldValue>
          </FormField>
        </>
      )}

      <FormField
        className={styles["revisions-retention"]}
        testId="revisions-retention"
      >
        <div
          data-testid="storage-retention-description"
          className={manageStorageProfileStyles.question}
        >
          How would you like to handle a user’s file revisions?
          <TooltipQuestion>
            Myota can store file versions for users to retrieve, if needed. If
            you allow this, parameters for deletion can be set.
          </TooltipQuestion>
        </div>
        <FormFieldValue className={styles["retention-mode"]}>
          <Select
            testId="retention-mode"
            value={props.revisionsMode}
            placeholder="Choose one.."
            disabled={props.disabled}
            options={retentionOptions}
            onChange={revisionsMode => onFieldChanged({ revisionsMode })}
          />
        </FormFieldValue>
      </FormField>

      {props.revisionsMode === RetentionPolicy.RetainAndThenDelete && (
        <>
          <FormField
            className={clsx(
              styles["retention-param"],
              !props.isRevisionsMbEnabled && styles["retention-param--disabled"]
            )}
          >
            <FormFieldLabel className={styles["retention-param-label"]}>
              <Checkbox
                testId="retention-size"
                disabled={props.disabled}
                label="Automatically clear file revisions when they exceed:"
                checked={props.isRevisionsMbEnabled}
                onChange={value =>
                  onFieldChanged({
                    isRevisionsMbEnabled:
                      value || !props.isRevisionsDaysEnabled,
                  })
                }
              />
              <TooltipQuestion>{REVISIONS_SPACE_TOOLTIP}</TooltipQuestion>
            </FormFieldLabel>
            <FormFieldValue className={styles["retention-param-value"]}>
              <SpaceSelector
                testId="deleted-files-exceed-selector"
                hideMb={true}
                value={props.revisionsMb}
                onChange={revisionsMb => onFieldChanged({ revisionsMb })}
                disabled={props.disabled || !props.isRevisionsMbEnabled}
                tooltip={REVISIONS_SPACE_TOOLTIP}
              />
              {props.errors.revisionsMb && (
                <div className="error-text">{props.errors.revisionsMb}</div>
              )}
            </FormFieldValue>
          </FormField>

          <FormField
            className={clsx(
              styles["retention-param"],
              !props.isRevisionsDaysEnabled &&
                styles["retention-param--disabled"]
            )}
          >
            <FormFieldLabel className={styles["retention-param-label"]}>
              <Checkbox
                testId="retention-age"
                disabled={props.disabled}
                label="Automatically clear file revisions older than:"
                checked={props.isRevisionsDaysEnabled}
                onChange={value =>
                  onFieldChanged({
                    isRevisionsDaysEnabled:
                      value || !props.isRevisionsMbEnabled,
                  })
                }
              />
              <TooltipQuestion>{REVISIONS_TIME_TOOLTIP}</TooltipQuestion>
            </FormFieldLabel>
            <FormFieldValue className={styles["retention-param-value"]}>
              <TimeIntervalSelector
                tooltip={REVISIONS_TIME_TOOLTIP}
                value={props.revisionsDays}
                disabled={props.disabled || !props.isRevisionsDaysEnabled}
                onChange={revisionsDays => onFieldChanged({ revisionsDays })}
              />
              {props.errors.revisionsDays && (
                <div className="error-text">{props.errors.revisionsDays}</div>
              )}
            </FormFieldValue>
          </FormField>
        </>
      )}
    </SidePanelSection>
  );
}
